import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { outputIfNoVariables } from '../../../utilities/misc';
import { rem } from '../../../utilities/style';
import { body, headers, richTextStyles } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';

import Button from '../../buttons/Button';
import Image from '../../Image';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: ${rem(24)};
  height: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in;

  ${media('>tiny')} {
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: ${rem(40)};
  }

  ${media('>small')} {
    justify-content: space-between;
  }

  ${({ active }) => active && `
    height: auto;
    opacity: 1;
    visibility: visible;
  `}
`;

const TextContent = styled.div`
  ${media('>small')} {
    flex: 0 2 ${rem(560)};
    margin: auto 0;
  }
`;

const Headline = styled.h3`
  ${headers.h3}
  margin-bottom: ${rem(16)};
  color: ${colors.blue['800']};
  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Text = styled.div`
  ${richTextStyles}
  ${body.regular}
  color: ${colors.gray['600']};
  margin-bottom: ${rem(32)};
`;

const ImageContainer = styled.div`
  ${media('>small')} {
    flex: 0 1 ${rem(360)};
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  border-radius: ${rem(10)};
`;

const VerticalTab = ({
  active,
  headline,
  text,
  primaryCtaText,
  primaryCtaLink,
  image,
}) => (
  <Container active={active}>
    <ImageContainer>
      {image && <StyledImage image={image} />}
    </ImageContainer>
    <TextContent>
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      <Text>
        {text && <TextRenderer text={text} richText />}
      </Text>
      {primaryCtaText && primaryCtaLink && (
        <Button
          to={primaryCtaLink}
          aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
        >
          <TextRenderer text={primaryCtaText} />
        </Button>
      )}
    </TextContent>
  </Container>
);

VerticalTab.propTypes = {
  active: PropTypes.bool,
  headline: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  image: Image.propTypes.image,
};

export const query = graphql`
  fragment VerticalTabFragment on ContentfulVerticalTab {
    tabIcon {
      gatsbyImageData(width: 128, height: 50, quality: 75)
      description
    }
    tabText
    headline
    text {
      raw
    }
    primaryCtaText
    primaryCtaLink
    image {
      gatsbyImageData(width: 360, quality: 75)
      description
    }
  }
`;

export default VerticalTab;
